import { useEffect, useMemo, useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import useFileStore from '../../stores/fileStore';
import usePendingChangesStore from '../../stores/pendingChangesStore';
import { getErrorMessage } from '../../utils/errors';
import { isValidProjectId } from '../../utils/projectIds';
import { compareProjectFilesByOrder } from '../../../shared/projectFileOrder';
import {
  CHALLENGE_PROJECT_DRAFT_PATH,
  CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
  CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import './DraftReviewPage.css';

/* ── Constants ── */
const TABS = ['overview', 'issues', 'fixes', 'story-bible', 'history'];
const TAB_KEYS = {
  overview: 'draftReview.tabs.overview',
  issues: 'draftReview.tabs.issues',
  fixes: 'draftReview.tabs.fixes',
  'story-bible': 'draftReview.tabs.storyBible',
  history: 'draftReview.tabs.history',
};

const STEP_ORDER = [
  { key: 'story_bible', labelKey: 'draftReview.steps.compileStoryBible' },
  { key: 'analyze', labelKey: 'draftReview.steps.analyzeManuscript' },
  { key: 'fixes', labelKey: 'draftReview.steps.generateFixes' },
];

const STEP_STATUS_KEYS = {
  pending: 'draftReview.stepStatus.pending',
  running: 'draftReview.stepStatus.running',
  completed: 'draftReview.stepStatus.completed',
  failed: 'draftReview.stepStatus.failed',
};

const RUN_STATUS_KEYS = {
  running: 'draftReview.runStatus.running',
  completed: 'draftReview.runStatus.completed',
  partial: 'draftReview.runStatus.partial',
  failed: 'draftReview.runStatus.failed',
};

const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };

/* ── Inline SVG Icons ── */
const IconCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const IconSpinner = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const IconCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
  </svg>
);

const IconXCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const IconWarning = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconFile = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const STEP_ICON_MAP = {
  completed: <span className="dr7-step-icon icon-completed"><IconCheck /></span>,
  running: <span className="dr7-step-icon icon-running"><IconSpinner /></span>,
  pending: <span className="dr7-step-icon icon-pending"><IconCircle /></span>,
  failed: <span className="dr7-step-icon icon-failed"><IconXCircle /></span>,
};

/* ── Helpers ── */
const parseIssuesPayload = (raw) => {
  if (!raw) return { topActions: [], issues: [], followUpConflicts: [] };
  try {
    const parsed = JSON.parse(raw);
    return {
      topActions: Array.isArray(parsed?.topActions) ? parsed.topActions.filter((i) => typeof i === 'string') : [],
      issues: Array.isArray(parsed?.issues) ? parsed.issues : [],
      followUpConflicts: Array.isArray(parsed?.followUpConflicts) ? parsed.followUpConflicts : [],
    };
  } catch {
    return { topActions: [], issues: [], followUpConflicts: [] };
  }
};

const parseFixesPayload = (raw) => {
  if (!raw) return { chapterFixes: [], crossChapterConflicts: [] };
  try {
    const parsed = JSON.parse(raw);
    return {
      chapterFixes: Array.isArray(parsed?.chapterFixes) ? parsed.chapterFixes : [],
      crossChapterConflicts: Array.isArray(parsed?.crossChapterConflicts) ? parsed.crossChapterConflicts : [],
    };
  } catch {
    return { chapterFixes: [], crossChapterConflicts: [] };
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

/* ═══════ Component ═══════ */
function DraftReviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;
  const [searchParams, setSearchParams] = useSearchParams();

  /* Convex hooks */
  const startDraftReviewRun = useMutation(api.ai.startDraftReviewRun);
  const runDraftReviewStep = useAction(api.ai.runDraftReviewStep);
  const retryDraftReviewStep = useAction(api.ai.retryDraftReviewStep);
  const bulkUpdateContent = useMutation(api.files.bulkUpdateContent);

  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const files = useQuery(api.files.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const storyBible = useQuery(api.storyBibles.getCurrent, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const runs = useQuery(api.ai.listDraftReviewRuns, safeProjectId ? { projectId: safeProjectId, limit: 10 } : 'skip');

  const loadFiles = useFileStore((s) => s.loadFiles);
  const openFile = useFileStore((s) => s.openFile);
  const addPendingChange = usePendingChangesStore((s) => s.addPendingChange);

  /* Local state */
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const [selectedChapterPaths, setSelectedChapterPaths] = useState([]);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const [stepLoading, setStepLoading] = useState({ analyze: false, story_bible: false, fixes: false });
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [chapterFilter, setChapterFilter] = useState('all');
  const [selectedFixPaths, setSelectedFixPaths] = useState([]);
  const [scopeOpen, setScopeOpen] = useState(false);

  /* Derived */
  const activeTabParam = searchParams.get('tab');
  const activeTab = TABS.includes(activeTabParam) ? activeTabParam : 'overview';

  const chapterFiles = useMemo(() => {
    if (!files) return [];
    const hasCanonicalChallengeSubmission = files.some((f) => f.type === 'file' && f.path === CHALLENGE_PROJECT_SUBMISSION_PATH);
    return files
      .filter((f) => {
        if (f.type !== 'file' || typeof f.content !== 'string' || f.content.trim().length === 0) return false;
        return (f.path.startsWith('Chapters/') && f.path !== CHALLENGE_PROJECT_DRAFT_PATH) ||
          (!hasCanonicalChallengeSubmission && f.path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH);
      })
      .sort(compareProjectFilesByOrder);
  }, [files]);

  const emptyChapterCount = useMemo(() => {
    if (!files) return 0;
    const hasCanonicalChallengeSubmission = files.some((f) => f.type === 'file' && f.path === CHALLENGE_PROJECT_SUBMISSION_PATH);
    return files.filter((f) => {
      if (f.type !== 'file') return false;
      const isManuscriptPath = (f.path.startsWith('Chapters/') && f.path !== CHALLENGE_PROJECT_DRAFT_PATH) ||
        (!hasCanonicalChallengeSubmission && f.path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH);
      return isManuscriptPath && (!(typeof f.content === 'string') || f.content.trim().length === 0);
    }).length;
  }, [files]);

  const chapterFileMap = useMemo(() => new Map(chapterFiles.map((c) => [c.path, c])), [chapterFiles]);

  const scopedContextFiles = useMemo(() => {
    if (!files) return [];
    return files.filter((f) => {
      if (f.type !== 'file') return false;
      if (f.path === 'outline.md') return true;
      return f.path.startsWith('Chapters/') || f.path.startsWith('Notes/') || f.path.startsWith('World Building/') || f.path.startsWith('Characters/');
    });
  }, [files]);

  const runForDisplay = useMemo(() => {
    if (!runs || runs.length === 0) return null;
    if (activeRunId) {
      const found = runs.find((r) => r._id === activeRunId);
      if (found) return found;
    }
    return runs[0];
  }, [runs, activeRunId]);

  const estimate = useQuery(
    api.ai.estimateDraftReview,
    safeProjectId ? { projectId: safeProjectId, chapterPaths: selectedChapterPaths } : 'skip',
  );

  /* Guard: invalid project */
  if (!safeProjectId) {
    return (
      <main className="draft-review-v7 loading">
        <div className="dr7-empty">
          {t('common.invalidProjectLink')}
          <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.backToProjects')}</button>
        </div>
      </main>
    );
  }

  const issuesPayload = useMemo(() => parseIssuesPayload(runForDisplay?.issuesJson), [runForDisplay?.issuesJson]);
  const fixesPayload = useMemo(() => parseFixesPayload(runForDisplay?.fixesJson), [runForDisplay?.fixesJson]);

  const sortedIssues = useMemo(() => {
    return [...issuesPayload.issues].sort((a, b) => {
      const d = (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99);
      if (d !== 0) return d;
      return (a.chapterPath || '').localeCompare(b.chapterPath || '');
    });
  }, [issuesPayload.issues]);

  const categoryOptions = useMemo(() => {
    const s = new Set();
    for (const i of sortedIssues) if (i?.category) s.add(i.category);
    return Array.from(s).sort();
  }, [sortedIssues]);

  const chapterOptions = useMemo(() => {
    const s = new Set();
    for (const i of sortedIssues) if (i?.chapterPath) s.add(i.chapterPath);
    return Array.from(s).sort();
  }, [sortedIssues]);

  const filteredIssues = useMemo(() => {
    return sortedIssues.filter((i) => {
      if (severityFilter !== 'all' && i.severity !== severityFilter) return false;
      if (categoryFilter !== 'all' && i.category !== categoryFilter) return false;
      if (chapterFilter !== 'all' && i.chapterPath !== chapterFilter) return false;
      return true;
    });
  }, [sortedIssues, severityFilter, categoryFilter, chapterFilter]);

  const severityCounts = useMemo(() => {
    return sortedIssues.reduce((acc, i) => {
      if (i?.severity === 'high') acc.high += 1;
      if (i?.severity === 'medium') acc.medium += 1;
      if (i?.severity === 'low') acc.low += 1;
      return acc;
    }, { high: 0, medium: 0, low: 0 });
  }, [sortedIssues]);

  const storyBibleHtml = useMemo(() => {
    if (!storyBible?.markdown) return '';
    const parsed = marked.parse(storyBible.markdown);
    return DOMPurify.sanitize(typeof parsed === 'string' ? parsed : '');
  }, [storyBible?.markdown]);

  /* Effects */
  useEffect(() => {
    if (!runs || runs.length === 0) { setActiveRunId(null); return; }
    if (!activeRunId) { setActiveRunId(runs[0]._id); return; }
    if (!runs.some((r) => r._id === activeRunId)) setActiveRunId(runs[0]._id);
  }, [runs, activeRunId]);

  useEffect(() => {
    if (!hasInitializedSelection && chapterFiles.length > 0) {
      setSelectedChapterPaths(chapterFiles.map((c) => c.path));
      setHasInitializedSelection(true);
    }
  }, [chapterFiles, hasInitializedSelection]);

  useEffect(() => {
    setSelectedChapterPaths((cur) => {
      const next = cur.filter((p) => chapterFileMap.has(p));
      return next.length === cur.length ? cur : next;
    });
  }, [chapterFileMap]);

  useEffect(() => {
    setSelectedFixPaths(fixesPayload.chapterFixes.map((f) => f.chapterPath));
  }, [runForDisplay?._id, fixesPayload.chapterFixes]);

  /* Handlers */
  const setTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const setStepLoadingState = (step, value) => setStepLoading((c) => ({ ...c, [step]: value }));

  const runSingleStep = async (runId, step, retry = false) => {
    setStepLoadingState(step, true);
    try {
      const action = retry ? retryDraftReviewStep : runDraftReviewStep;
      const updatedRun = await action({ runId, step });
      setActiveRunId(updatedRun._id);
      if (step === 'analyze') setTab('issues');
      else if (step === 'story_bible') setTab('story-bible');
      else if (step === 'fixes') setTab('fixes');
      return updatedRun;
    } finally {
      setStepLoadingState(step, false);
    }
  };

  const toggleSelectedChapter = (path) => {
    setSelectedChapterPaths((c) => c.includes(path) ? c.filter((p) => p !== path) : [...c, path].sort((a, b) => a.localeCompare(b)));
  };

  const handleSelectAllChapters = () => setSelectedChapterPaths(chapterFiles.map((c) => c.path));
  const handleClearChapterSelection = () => setSelectedChapterPaths([]);

  const handleStartRun = async () => {
    if (!safeProjectId || isStartingRun) return;
    setErrorMessage('');
    setIsStartingRun(true);
    try {
      const result = await startDraftReviewRun({ projectId: safeProjectId, chapterPaths: selectedChapterPaths });
      setActiveRunId(result.runId);
      setIsConfirmOpen(false);
      setTab('overview');
      void (async () => {
        try {
          let currentRunId = result.runId;
          for (const step of ['story_bible', 'analyze', 'fixes']) {
            const updatedRun = await runSingleStep(currentRunId, step, false);
            currentRunId = updatedRun._id;
          }
        } catch (e) {
          console.error('Pipeline auto-run failed:', e);
          setErrorMessage(getErrorMessage(e, 'Draft Review run started, but the pipeline did not complete. Retry the failed step.'));
        }
      })();
    } catch (err) {
      console.error('Draft Review start failed:', err);
      setErrorMessage(getErrorMessage(err, 'Failed to start Draft Review run.'));
    } finally {
      setIsStartingRun(false);
    }
  };

  const handleRunStep = async (step, retry = false) => {
    if (!runForDisplay || stepLoading[step] || isStartingRun) return;
    setErrorMessage('');
    try { await runSingleStep(runForDisplay._id, step, retry); }
    catch (err) { setErrorMessage(getErrorMessage(err, `Failed to run ${step} step.`)); }
  };

  const handleToggleFix = (path) => {
    setSelectedFixPaths((c) => c.includes(path) ? c.filter((p) => p !== path) : [...c, path].sort((a, b) => a.localeCompare(b)));
  };

  const queueFixes = (paths) => {
    if (!safeProjectId || paths.length === 0) return;
    const selected = fixesPayload.chapterFixes.filter((f) => paths.includes(f.chapterPath));
    if (selected.length === 0) return;
    for (const fix of selected) {
      const file = chapterFileMap.get(fix.chapterPath);
      addPendingChange(fix.chapterPath, file?.content || '', fix.revisedContent, fix.reason || 'Draft Review chapter update');
    }
    openFile(selected[0].chapterPath);
    navigate(`/projects/${safeProjectId}/editor`);
  };

  const applyFixes = async (paths) => {
    if (!safeProjectId || paths.length === 0 || isApplying) return;
    const updates = fixesPayload.chapterFixes
      .filter((f) => paths.includes(f.chapterPath))
      .map((f) => ({ path: f.chapterPath, content: f.revisedContent }));
    if (updates.length === 0) return;
    setErrorMessage('');
    setIsApplying(true);
    try {
      await bulkUpdateContent({ projectId: safeProjectId, updates });
      await loadFiles(safeProjectId);
    } catch (err) {
      setErrorMessage(getErrorMessage(err, 'Failed to apply chapter fixes.'));
    } finally {
      setIsApplying(false);
    }
  };

  const openStoryBibleInEditor = async () => {
    if (!safeProjectId) return;
    await loadFiles(safeProjectId);
    openFile('Notes/story-bible.md');
    navigate(`/projects/${safeProjectId}/editor`);
  };

  /* Loading guards */
  if (project === undefined || files === undefined || runs === undefined || storyBible === undefined) {
    return (
      <main className="draft-review-v7 loading" aria-busy="true">
        <div className="dr7-empty">{t('draftReview.loadingWorkspace')}</div>
      </main>
    );
  }
  if (!project) {
    return (
      <main className="draft-review-v7 loading">
        <div className="dr7-empty">{t('common.projectNotFound')}</div>
      </main>
    );
  }

  /* Computed */
  const runStatuses = {
    analyze: runForDisplay?.analyzeStatus ?? 'pending',
    story_bible: runForDisplay?.storyBibleStatus ?? 'pending',
    fixes: runForDisplay?.fixesStatus ?? 'pending',
  };

  const selectedFixCount = selectedFixPaths.length;
  const estimatedCredits = estimate?.estimatedCredits ?? runForDisplay?.estimatedCredits ?? 0;
  const hasSelectedChapter = selectedChapterPaths.length > 0;

  const canRunStoryBible = runStatuses.story_bible === 'pending';
  const canRetryStoryBible = runStatuses.story_bible === 'failed';
  const canRunAnalyze = runStatuses.story_bible === 'completed' && runStatuses.analyze === 'pending';
  const canRetryAnalyze = runStatuses.analyze === 'failed';
  const canRunFixes = runStatuses.story_bible === 'completed' && runStatuses.analyze === 'completed' && runStatuses.fixes === 'pending';
  const canRetryFixes = runStatuses.fixes === 'failed';

  const completedStepCount = STEP_ORDER.filter((s) => runStatuses[s.key] === 'completed').length;
  const isAnyStepRunning = Object.values(stepLoading).some(Boolean);
  const isRunning = isStartingRun || isAnyStepRunning;

  /* ═══════ Render ═══════ */
  return (
    <main className="draft-review-v7" aria-label={t('draftReview.aria.workspace')}>

      {/* ── Header ── */}
      <header className="dr7-header">
        <div className="dr7-header-left">
          <h1>{t('draftReview.title')} <span>— {project.title}</span></h1>
        </div>
        <div className="dr7-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}`)}>{t('common.back')}</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}/editor`)}>{t('common.editor')}</button>
        </div>
      </header>

      {/* ── Tabs ── */}
      <nav className="dr7-tabs" role="tablist" aria-label={t('draftReview.aria.tabs')}>
        {TABS.map((tab) => {
          const count = tab === 'issues' ? sortedIssues.length
            : tab === 'fixes' ? fixesPayload.chapterFixes.length
              : tab === 'history' ? runs.length
                : null;
          return (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              className={`dr7-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setTab(tab)}
            >
              {t(TAB_KEYS[tab])}
              {count !== null ? <strong>{count}</strong> : null}
            </button>
          );
        })}
      </nav>

      {/* ── Error ── */}
      {errorMessage ? <section className="dr7-error" role="alert">{errorMessage}</section> : null}

      {/* ── Tab Surface ── */}
      <section className="dr7-surface">

        {/* ══ Overview ══ */}
        {activeTab === 'overview' ? (
          <div className="dr7-panel-fade" role="tabpanel" aria-label={t('draftReview.tabs.overview')}>
            {/* Pipeline Steps */}
            <div className="dr7-pipeline">
              {STEP_ORDER.map((step) => {
                const status = runStatuses[step.key];
                const isLoadingStep = stepLoading[step.key];
                let canRun = false;
                let canRetry = false;
                if (step.key === 'story_bible') { canRun = canRunStoryBible; canRetry = canRetryStoryBible; }
                else if (step.key === 'analyze') { canRun = canRunAnalyze; canRetry = canRetryAnalyze; }
                else { canRun = canRunFixes; canRetry = canRetryFixes; }

                return (
                  <div key={step.key} className={`dr7-step-card status-${status}`}>
                    {STEP_ICON_MAP[status] || STEP_ICON_MAP.pending}
                    <span className="dr7-step-label">{t(step.labelKey)}</span>
                    <div className="dr7-step-actions">
                      {(canRun || isLoadingStep) ? (
                        <button
                          className="btn btn-primary"
                          onClick={() => handleRunStep(step.key, false)}
                          disabled={!runForDisplay || !canRun || isLoadingStep || isStartingRun}
                        >
                          {isLoadingStep ? `${t('common.running')}` : t('common.run')}
                        </button>
                      ) : null}
                      {canRetry ? (
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleRunStep(step.key, true)}
                          disabled={isLoadingStep || isStartingRun}
                        >
                          {t('common.retry')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Run Bar */}
            <div className="dr7-run-bar">
              {isRunning ? (
                <button className="btn btn-primary" disabled>
                  <span className="dr7-spinner-inline" />
                  {t('draftReview.runningReview')}
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={() => setIsConfirmOpen(true)}
                  disabled={!hasSelectedChapter}
                >
                  {t('draftReview.startNewRun')}
                </button>
              )}
              <span className="muted-text" style={{ fontSize: '0.76rem' }}>
                {t('draftReview.stepsCount', { completed: completedStepCount, total: STEP_ORDER.length })} · {t('draftReview.chaptersSelected', { count: selectedChapterPaths.length })} · {t('draftReview.estimatedCredits', { credits: estimatedCredits.toFixed(1) })}
              </span>
            </div>

            {/* Scope (collapsible) */}
            <div className="dr7-scope-section">
              <button className="dr7-scope-toggle" onClick={() => setScopeOpen(!scopeOpen)}>
                <span className={`dr7-chevron ${scopeOpen ? 'open' : ''}`}>▸</span>
                {t('draftReview.reviewScope')}
                <span className="dr7-scope-count">{t('draftReview.chaptersCount', { selected: selectedChapterPaths.length, total: chapterFiles.length })}</span>
              </button>
              {scopeOpen ? (
                <div className="dr7-scope-body">
                  <div className="dr7-scope-actions">
                    <button className="btn btn-secondary" onClick={handleSelectAllChapters}>{t('common.selectAll')}</button>
                    <button className="btn btn-secondary" onClick={handleClearChapterSelection}>{t('common.clear')}</button>
                  </div>
                  <div className="dr7-chapter-list">
                    {chapterFiles.length === 0 ? (
                      <p className="muted-text">
                        {emptyChapterCount > 0
                          ? t('draftReview.emptyChaptersHint')
                          : t('draftReview.noChapterFiles')}
                      </p>
                    ) : (
                      chapterFiles.map((c) => (
                        <label key={c.path} className="dr7-chapter-item">
                          <input type="checkbox" checked={selectedChapterPaths.includes(c.path)} onChange={() => toggleSelectedChapter(c.path)} />
                          <span>{c.path}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {emptyChapterCount > 0 ? (
                    <p className="muted-text" style={{ marginTop: 8 }}>
                      {t('draftReview.emptyChaptersSkipped', { count: emptyChapterCount, plural: emptyChapterCount === 1 ? '' : 's' })}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Overview metrics & summary */}
            <div className="dr7-overview-grid">
              <article className="dr7-overview-card">
                <h2>{t('draftReview.corpusMetrics')} <span className="dr7-pill-muted">{t('draftReview.live')}</span></h2>
                <div className="dr7-mini-stats">
                  <div className="dr7-mini-stat"><span>{t('draftReview.projectChapters')}</span><strong>{chapterFiles.length}</strong></div>
                  <div className="dr7-mini-stat"><span>{t('draftReview.selected')}</span><strong>{selectedChapterPaths.length}</strong></div>
                  <div className="dr7-mini-stat"><span>{t('draftReview.scopeFiles')}</span><strong>{scopedContextFiles.length}</strong></div>
                  <div className="dr7-mini-stat"><span>{t('draftReview.runStatusLabel')}</span><strong>{runForDisplay?.status ? t(RUN_STATUS_KEYS[runForDisplay.status]) : t('draftReview.noRun')}</strong></div>
                </div>
                <div className="dr7-severity-strip">
                  <span className="sev-high">{t('draftReview.severity.high')} {severityCounts.high}</span>
                  <span className="sev-medium">{t('draftReview.severity.medium')} {severityCounts.medium}</span>
                  <span className="sev-low">{t('draftReview.severity.low')} {severityCounts.low}</span>
                </div>
              </article>
              <article className="dr7-overview-card">
                <h2>{t('draftReview.latestSummary')}</h2>
                <p>{runForDisplay?.summary || t('draftReview.startRunPrompt')}</p>
                {issuesPayload.topActions.length > 0 ? (
                  <div className="dr7-top-actions">
                    <h3>{t('draftReview.topActions')}</h3>
                    <ol>{issuesPayload.topActions.map((a) => <li key={a}>{a}</li>)}</ol>
                  </div>
                ) : null}
              </article>
            </div>
          </div>
        ) : null}

        {/* ══ Issues ══ */}
        {activeTab === 'issues' ? (
          <div className="dr7-panel-fade" role="tabpanel" aria-label={t('draftReview.tabs.issues')}>
            {/* Severity Cards */}
            <div className="dr7-severity-row">
              <div className="dr7-severity-card sev-critical">
                <span className="sev-count">{severityCounts.high}</span>
                <span className="sev-label">{t('draftReview.severity.critical')}</span>
              </div>
              <div className="dr7-severity-card sev-warnings">
                <span className="sev-count">{severityCounts.medium}</span>
                <span className="sev-label">{t('draftReview.severity.warnings')}</span>
              </div>
              <div className="dr7-severity-card sev-info">
                <span className="sev-count">{severityCounts.low}</span>
                <span className="sev-label">{t('draftReview.severity.info')}</span>
              </div>
            </div>

            {/* Filters */}
            <div className="dr7-filters">
              <div className="dr7-filter">
                <span className="dr7-filter-label">{t('draftReview.severityFilter')}</span>
                <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
                  <option value="all">{t('common.all')}</option>
                  <option value="high">{t('draftReview.severity.high')}</option>
                  <option value="medium">{t('draftReview.severity.medium')}</option>
                  <option value="low">{t('draftReview.severity.low')}</option>
                </select>
              </div>
              <div className="dr7-filter">
                <span className="dr7-filter-label">{t('draftReview.categoryFilter')}</span>
                <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <option value="all">{t('common.all')}</option>
                  {categoryOptions.map((c) => <option key={c} value={c}>{c.replaceAll('_', ' ')}</option>)}
                </select>
              </div>
              <div className="dr7-filter">
                <span className="dr7-filter-label">{t('draftReview.chapterFilter')}</span>
                <select value={chapterFilter} onChange={(e) => setChapterFilter(e.target.value)}>
                  <option value="all">{t('common.all')}</option>
                  {chapterOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Issue List */}
            {filteredIssues.length === 0 ? (
              <p className="dr7-empty">{t('draftReview.noIssuesMatch')}</p>
            ) : (
              <div className="dr7-issue-list">
                {filteredIssues.map((issue) => (
                  <article key={`${issue.id}-${issue.chapterPath}`} className={`dr7-issue-card severity-${issue.severity}`}>
                    <div className="dr7-issue-head">
                      <span className="dr7-issue-warn-icon"><IconWarning /></span>
                      <span className="dr7-issue-title">{issue.title}</span>
                      <span className="dr7-issue-cat">{issue.category.replaceAll('_', ' ')}</span>
                    </div>
                    <p className="dr7-issue-desc">{issue.description}</p>
                    {issue.evidence ? <p className="dr7-issue-evidence"><strong>{t('draftReview.evidence')}</strong>{issue.evidence}</p> : null}
                    {issue.suggestedFix ? <p className="dr7-issue-fix"><strong>{t('draftReview.suggestedFix')}</strong>{issue.suggestedFix}</p> : null}
                    <div className="dr7-issue-footer">
                      <span>{issue.chapterPath}</span>
                      <div className="dr7-issue-footer-actions">
                        {fixesPayload.chapterFixes.some((f) => f.chapterPath === issue.chapterPath) ? (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => {
                              setActiveTab('fixes');
                            }}>
                              {t('draftReview.jumpToFix')}
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => queueFixes([issue.chapterPath])}>
                              {t('draftReview.queueDiff')}
                            </button>
                          </>
                        ) : (
                          <span className="dr7-issue-no-fix-hint">{t('draftReview.noFixGenerated')}</span>
                        )}
                        <button className="dr7-issue-file-link" onClick={() => {
                          openFile(issue.chapterPath);
                          navigate(`/projects/${safeProjectId}/editor`);
                        }}>
                          <IconFile /> {t('draftReview.editInEditor')}
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {issuesPayload.followUpConflicts.length > 0 ? (
              <div className="dr7-conflict-box">
                <h3>{t('draftReview.crossChapterConflicts')}</h3>
                <ul>
                  {issuesPayload.followUpConflicts.map((c, i) => (
                    <li key={`${c.chapterPath}-${i}`}><strong>{c.chapterPath}:</strong> {c.reason}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ══ Fixes ══ */}
        {activeTab === 'fixes' ? (
          <div className="dr7-panel-fade" role="tabpanel" aria-label={t('draftReview.tabs.fixes')}>
            <div className="dr7-fix-toolbar">
              <span>{t('draftReview.selectedFixes', { count: selectedFixCount })}</span>
              <div className="dr7-fix-toolbar-actions">
                <button className="btn btn-secondary" onClick={() => setSelectedFixPaths(fixesPayload.chapterFixes.map((f) => f.chapterPath))}>{t('common.selectAll')}</button>
                <button className="btn btn-secondary" onClick={() => setSelectedFixPaths([])}>{t('common.clear')}</button>
                <button className="btn btn-primary" onClick={() => queueFixes(selectedFixPaths)} disabled={selectedFixCount === 0}>{t('draftReview.reviewSelected')}</button>
                <button className="btn btn-primary" onClick={() => applyFixes(selectedFixPaths)} disabled={selectedFixCount === 0 || isApplying}>{isApplying ? t('draftReview.applyingFixes') : t('draftReview.applySelected')}</button>
              </div>
            </div>

            <p className="dr7-fix-guidance">{t('draftReview.fixGuidance')}</p>

            {fixesPayload.chapterFixes.length === 0 ? (
              <p className="dr7-empty">{t('draftReview.noRewrites')}</p>
            ) : (
              <div className="dr7-fix-list">
                {fixesPayload.chapterFixes.map((fix) => (
                  <article key={fix.chapterPath} className="dr7-fix-item">
                    <label className="dr7-fix-selector">
                      <input type="checkbox" checked={selectedFixPaths.includes(fix.chapterPath)} onChange={() => handleToggleFix(fix.chapterPath)} />
                      <span>{fix.chapterPath}</span>
                    </label>
                    <p>{fix.reason}</p>
                    <div className="dr7-fix-item-actions">
                      <button className="btn btn-primary" onClick={() => queueFixes([fix.chapterPath])}>{t('draftReview.reviewChanges')}</button>
                      <button className="btn btn-primary" onClick={() => applyFixes([fix.chapterPath])} disabled={isApplying}>{t('draftReview.applyFix')}</button>
                      <button className="btn btn-secondary" onClick={() => {
                        openFile(fix.chapterPath);
                        navigate(`/projects/${safeProjectId}/editor`);
                      }}>{t('draftReview.editManually')}</button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {fixesPayload.crossChapterConflicts.length > 0 ? (
              <div className="dr7-conflict-box">
                <h3>{t('draftReview.crossChapterConflictsFlagged')}</h3>
                <ul>
                  {fixesPayload.crossChapterConflicts.map((c, i) => (
                    <li key={`${c.chapterPath}-${i}`}><strong>{c.chapterPath}:</strong> {c.reason}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ══ Story Bible ══ */}
        {activeTab === 'story-bible' ? (
          <div className="dr7-panel-fade" role="tabpanel" aria-label={t('draftReview.tabs.storyBible')}>
            <div className="dr7-story-bible-header">
              <div>
                <h2>{t('draftReview.storyBibleTitle')}</h2>
                <p className="dr7-bible-meta">
                  {storyBible
                    ? t('draftReview.storyBibleSource', { source: storyBible.source === 'manual' ? t('draftReview.storyBibleManual') : t('draftReview.storyBibleAI'), date: formatDateTime(storyBible.updatedAt) })
                    : t('draftReview.noStoryBibleYet')}
                </p>
              </div>
              <button className="btn btn-primary" onClick={openStoryBibleInEditor} disabled={!storyBible?.markdown}>{t('draftReview.openInEditor')}</button>
            </div>

            {storyBible?.source === 'manual' ? <div className="dr7-manual-pill">{t('draftReview.lastSyncedManual')}</div> : null}

            {storyBible?.markdown ? (
              <div className="dr7-bible-preview" dangerouslySetInnerHTML={{ __html: storyBibleHtml }} />
            ) : (
              <p className="muted-text">{t('draftReview.storyBibleMirror')} <code>Notes/story-bible.md</code>.</p>
            )}
          </div>
        ) : null}

        {/* ══ History ══ */}
        {activeTab === 'history' ? (
          <div className="dr7-panel-fade" role="tabpanel" aria-label={t('draftReview.tabs.history')}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', marginBottom: 16 }}>{t('draftReview.runHistory', { count: runs.length })}</h2>
            {runs.length === 0 ? (
              <p className="dr7-empty">{t('draftReview.noDraftReviewRuns')}</p>
            ) : (
              <div className="dr7-history-list">
                {runs.map((run) => (
                  <article key={run._id} className={`dr7-history-item ${run._id === runForDisplay?._id ? 'active' : ''}`}>
                    <div className="dr7-history-main">
                      <div>
                        <h3>{formatDateTime(run.createdAt)}</h3>
                        <p className="muted-text">
                          {t(RUN_STATUS_KEYS[run.status])} · {t('draftReview.chaptersSelected', { count: run.scopeChapterPaths.length })}
                        </p>
                        <p className="muted-text">
                          {t('draftReview.tabs.storyBible')}: {t(STEP_STATUS_KEYS[run.storyBibleStatus])} · {t('draftReview.steps.analyzeManuscript')}: {t(STEP_STATUS_KEYS[run.analyzeStatus])} · {t('draftReview.tabs.fixes')}: {t(STEP_STATUS_KEYS[run.fixesStatus])}
                        </p>
                      </div>
                      <button className="btn btn-secondary" onClick={() => { setActiveRunId(run._id); setTab('overview'); }}>{t('draftReview.openSnapshot')}</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </section>

      {/* ── Confirm Modal ── */}
      {isConfirmOpen ? (
        <div className="dr7-modal-backdrop" role="presentation" onClick={() => setIsConfirmOpen(false)}>
          <div className="dr7-modal" role="dialog" aria-modal="true" aria-label={t('draftReview.confirmRunTitle')} onClick={(e) => e.stopPropagation()}>
            <h2>{t('draftReview.confirmRunTitle')}</h2>
            <p>{t('draftReview.estimatedCreditsLabel')}<strong>{estimatedCredits.toFixed(1)}</strong></p>
            <p>{t('draftReview.selectedChaptersLabel')}<strong>{selectedChapterPaths.length}</strong></p>
            <ul className="dr7-modal-list">
              {selectedChapterPaths.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <div className="dr7-modal-actions">
              <button className="btn btn-secondary" onClick={() => setIsConfirmOpen(false)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleStartRun} disabled={selectedChapterPaths.length === 0 || isStartingRun}>
                {isStartingRun ? t('draftReview.starting') : t('draftReview.confirmAndStart')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

export default DraftReviewPage;
